import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { guardedLocalStorage } from './guardedLocalStorage';

/** How long a draft is offered back before it is quietly dropped. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface FormDraft<V> {
  values: V;
  /** When the person first typed into this form; shown in the restore note. */
  startedAt: string;
  updatedAt: string;
}

export function isDraftFresh(draft: { updatedAt: string }, now: Date): boolean {
  const updated = new Date(draft.updatedAt).getTime();
  return (
    Number.isFinite(updated) && now.getTime() - updated <= DRAFT_MAX_AGE_MS
  );
}

export interface FormDraftState<V> {
  drafts: Record<string, FormDraft<V>>;
  saveDraft: (scope: string, values: V) => void;
  /** The draft worth offering for `scope`; a stale one is dropped here. */
  takeDraft: (scope: string) => FormDraft<V> | undefined;
  clearDraft: (scope: string) => void;
  /** The form moved scope mid-edit (a provider switch): the draft follows. */
  moveDraft: (from: string, to: string, values: V) => void;
}

function without<T>(record: Record<string, T>, key: string) {
  return Object.fromEntries(
    Object.entries(record).filter(([candidate]) => candidate !== key),
  );
}

/**
 * A store of what was typed into a form and not yet saved, one draft per
 * scope (spec 124 R4).
 *
 * The connection form (spec 123) and the trigger form (spec 124) keep their
 * drafts through this one mechanism, so the two behave the same and cannot
 * drift: the entries kept on close, offered back with when they were started,
 * dropped after seven days, never including what `strip` removes. Persisted,
 * through storage that never throws. The task draft (spec 121) is a different
 * shape — it carries files and an upload queue — and stays its own.
 */
export function createFormDraftStore<V>({
  storageKey,
  strip = (values) => values,
}: {
  storageKey: string;
  /** Removes what must never be persisted, such as a typed-in credential. */
  strip?: (values: V) => V;
}) {
  return create<FormDraftState<V>>()(
    persist(
      (set, get) => ({
        drafts: {},
        saveDraft: (scope, values) => {
          const now = new Date().toISOString();
          const existing = get().drafts[scope];
          set({
            drafts: {
              ...get().drafts,
              [scope]: {
                values: strip(values),
                startedAt: existing?.startedAt ?? now,
                updatedAt: now,
              },
            },
          });
        },
        takeDraft: (scope) => {
          const draft = get().drafts[scope];
          if (!draft) return undefined;
          if (!isDraftFresh(draft, new Date())) {
            set({ drafts: without(get().drafts, scope) });
            return undefined;
          }
          return draft;
        },
        clearDraft: (scope) => {
          set({ drafts: without(get().drafts, scope) });
        },
        moveDraft: (from, to, values) => {
          const existing = get().drafts[from];
          const now = new Date().toISOString();
          set({
            drafts: {
              ...without(get().drafts, from),
              [to]: {
                values: strip(values),
                startedAt: existing?.startedAt ?? now,
                updatedAt: now,
              },
            },
          });
        },
      }),
      {
        name: storageKey,
        version: 1,
        storage: createJSONStorage(() => guardedLocalStorage),
        partialize: (state) => ({ drafts: state.drafts }),
      },
    ),
  );
}

export type FormDraftStore<V> = ReturnType<typeof createFormDraftStore<V>>;
